import { useEffect, useRef, useState } from "react";

import { getEmployeeRoles, getUserRoles, listRolesByIds } from "@/api/system/roles";
import type { RoleBatchListItemVO } from "@/api/system/roles/type";

import type { ResolvedRoleTerminal, RoleDetailSource, RoleDetailViewModel } from "./types";
import {
  normalizeRoleName,
  toResolvedTerminalFromBatchItem,
  toResolvedTerminalsFromUserGroup,
} from "./utils";

/**
 * 按 roleId 批量拉取角色详情并聚合为视图模型
 * @param roleIds 角色 ID 列表
 * @param terminalExtras 各 roleId 对应的绑定上下文（orgType 等）
 * @param fallbackRoleName 接口未返回时的角色名
 * @param orgName 组织名称
 */
const buildViewModelFromBatchRoles = (
  roles: RoleBatchListItemVO[],
  terminalExtras: Map<string, Partial<ResolvedRoleTerminal>>,
  fallbackRoleName: string,
  orgName?: string,
): RoleDetailViewModel => {
  const terminals = roles.map((item) =>
    toResolvedTerminalFromBatchItem(item, terminalExtras.get(item.id)),
  );
  return {
    roleName:
      roles[0]?.roleName ?? (fallbackRoleName.replace(/模板$/, "").trim() || fallbackRoleName),
    roleDesc: roles.find((item) => item.roleDesc)?.roleDesc,
    orgName,
    terminals,
  };
};

/**
 * 拉取并聚合角色详情与各端权限
 * @param open 弹窗是否打开
 * @param roleName 目标角色名
 * @param source 数据来源
 */
export const useRoleDetail = (open: boolean, roleName: string, source?: RoleDetailSource) => {
  /** 聚合后的角色详情 */
  const [data, setData] = useState<RoleDetailViewModel | null>(null);
  /** 加载中 */
  const [loading, setLoading] = useState(false);
  /** 请求序号，避免竞态 */
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!open || !source) {
      setData(null);
      setLoading(false);
      return;
    }
    const requestId = ++requestIdRef.current;
    const normalizedTarget = normalizeRoleName(roleName);

    /** 按 roleIds 批量查询角色（含各端权限数量） */
    const loadRolesByIds = async (
      roleIds: string[],
      terminalExtras: Map<string, Partial<ResolvedRoleTerminal>>,
      orgName?: string,
    ) => {
      if (roleIds.length === 0) {
        if (requestId !== requestIdRef.current) return;
        setData({
          roleName: roleName.replace(/模板$/, "").trim() || roleName,
          orgName,
          terminals: [],
        });
        return;
      }
      const res = await listRolesByIds({ roleIds });
      if (requestId !== requestIdRef.current) return;
      setData(buildViewModelFromBatchRoles(res.data ?? [], terminalExtras, roleName, orgName));
    };

    /** 按 source 解析 roleIds 后批量拉取 */
    const load = async () => {
      setLoading(true);
      setData(null);
      try {
        if (source.mode === "employee") {
          /** 拉取员工角色 */
          const res = await getEmployeeRoles({
            employeeId: source.employeeId,
            orgId: source.orgId,
          });
          if (requestId !== requestIdRef.current) return;
          const matched = (res.data ?? []).filter(
            (item) => normalizeRoleName(item.roleName) === normalizedTarget,
          );
          const terminalExtras = new Map<string, Partial<ResolvedRoleTerminal>>();
          matched.forEach((item) => {
            terminalExtras.set(item.roleId, {
              orgId: item.orgId,
              templateType: item.templateType,
              roleType: item.roleType,
            });
          });
          await loadRolesByIds(
            matched.map((item) => item.roleId),
            terminalExtras,
            matched[0]?.orgName,
          );
          return;
        }
        if (source.mode === "user") {
          /** 拉取账号角色 */
          const res = await getUserRoles({ userId: source.userId });
          if (requestId !== requestIdRef.current) return;
          const group = (res.data ?? []).find(
            (item) =>
              normalizeRoleName(item.roleName) === normalizedTarget &&
              (!source.orgId || item.orgId === source.orgId),
          );
          const bindings = group ? toResolvedTerminalsFromUserGroup(group) : [];
          const terminalExtras = new Map<string, Partial<ResolvedRoleTerminal>>();
          bindings.forEach((item) => {
            terminalExtras.set(item.roleId, {
              orgId: item.orgId,
              orgType: item.orgType,
              templateType: item.templateType,
              roleType: item.roleType,
            });
          });
          await loadRolesByIds(
            bindings.map((item) => item.roleId),
            terminalExtras,
            group?.orgName,
          );
          return;
        }
        // 直接按 roleIds 查询（邀请员工预览等场景）
        const terminalExtras = new Map<string, Partial<ResolvedRoleTerminal>>(
          source.roleIds.map((roleId) => [
            roleId,
            { orgId: source.orgId, orgType: source.orgType },
          ]),
        );
        await loadRolesByIds(source.roleIds, terminalExtras);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setData(null);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      requestIdRef.current += 1;
    };
  }, [open, roleName, source]);

  return { data, loading };
};
